/**
 * The input-command layer: keyboard, mouse-yoke and gamepad merged into one set of commands.
 *
 * Non-negotiable #7 — player control goes through a command layer. Nothing downstream of this
 * file knows whether a deflection came from a key, a thumbstick or a network packet, which is
 * what makes a replay, an autopilot and a remote peer interchangeable with a pilot.
 *
 * Key bindings follow the CLAUDE.md defaults:
 *   WASD / arrows pitch + roll · Q/E rudder · −/= throttle · F flaps · G gear · B brakes ·
 *   1–5 cameras · C cycle view · P pause · T time of day · M mute · F3 perf overlay · Esc menu
 * plus `,`/`.` for elevator trim, `Z`/`X` for rudder trim, `;`/`'` for aileron trim, `J` to
 * toggle the auto-coordination assist, and `Y` to toggle the mouse yoke.
 *
 * ## The rudder is not just the rudder keys
 *
 * `commands.yaw` is the sum of what the pilot asked for and what the coordination assist is
 * contributing, and downstream nothing knows the difference — which is the point of a command
 * layer. The assist yields as the pilot inputs more, so a deliberate slip is never an argument;
 * see `coordination.ts` for why it exists and what it refuses to do.
 *
 * `updateFixed(dt)`: yes — keyboard axes integrate on the fixed step, so how far the elevator
 * moves per second does not depend on the frame rate.
 */

import { KeyboardAxis, shapeAxis } from './axes.js';
import {
  AILERON_TRIM_AXIS,
  MOUSE_YOKE,
  PITCH_AXIS,
  ROLL_AXIS,
  RUDDER_TRIM_AXIS,
  THROTTLE_AXIS,
  TRIM_AXIS,
  YAW_AXIS,
} from './feel.js';
import { CoordinationAssist, type CoordinationFeedback } from './coordination.js';
import { GamepadInput, GAMEPAD_BUTTON, type GamepadState } from './gamepad.js';
import type { Keyboard } from './keyboard.js';
import type { ControlInputs } from '../sim/fdm.js';
import { clamp } from '../core/math.js';

/** Where the auto-coordination preference is remembered between sessions. */
const ASSIST_STORAGE_KEY = 'skysim.assist.coordination';

export interface FlightControlsOptions {
  /** Number of flap detents in the current aircraft. */
  flapDetentCount: number;
  /** Mouse-yoke sensitivity: fraction of full deflection per fraction of the window. */
  mouseYokeSensitivity?: number;
}

/**
 * The touch tier, as this layer sees it.
 *
 * An interface rather than an import of `TouchControls`, so the command layer keeps knowing
 * nothing about the DOM — which is the whole of non-negotiable #7. The same shape is what a
 * network peer's inputs would arrive in, and it is what makes the touch layer testable without a
 * screen.
 *
 * `throttle` is nullable and `active` exists for the same reason the gamepad has them: a control
 * that is not being touched must contribute *nothing*, not zero. A tablet with a keyboard case has
 * both, and an untouched stick that reported 0 would pin the elevator.
 */
export interface TouchSource {
  readonly active: boolean;
  readonly pitch: number;
  readonly roll: number;
  readonly yaw: number;
  readonly throttle: number | null;
  readonly brakes: boolean;
  readonly fire: boolean;
}

export interface ControlDiagnostics {
  /** Which device last moved something. */
  activeDevice: 'keyboard' | 'mouse' | 'gamepad' | 'touch';
  mouseYokeEnabled: boolean;
  gamepadConnected: boolean;
  gamepadId: string;
  /** Is the auto-coordination assist on? */
  coordinationEnabled: boolean;
  /** How much rudder the assist is contributing right now, −1…1. */
  coordinationOutput: number;
}

export class FlightControls {
  readonly commands: ControlInputs = {
    pitch: 0,
    roll: 0,
    yaw: 0,
    throttle: 0,
    pitchTrim: 0,
    yawTrim: 0,
    rollTrim: 0,
    flapDetent: 0,
    brakeLeft: 0,
    brakeRight: 0,
    parkingBrake: false,
    ignition: true,
    starter: false,
    /*
     * Gear down at spawn, which is where every aeroplane starts.
     *
     * This field did not exist until v1.1. `ControlInputs.gearDown` was declared optional in
     * `fdm.ts` and read as `(inputs.gearDown ?? true)`, and **nothing in the product ever assigned
     * it** — not this struct, not the gamepad, not the touch layer. So it defaulted to `true` a
     * hundred and twenty times a second and the Vandal's legs were lerped to "down" for ever. The
     * gear was not slow or gated; it was never commanded.
     */
    gearDown: true,
  };

  readonly diagnostics: ControlDiagnostics = {
    activeDevice: 'keyboard',
    mouseYokeEnabled: false,
    gamepadConnected: false,
    gamepadId: '',
    coordinationEnabled: true,
    coordinationOutput: 0,
  };

  // Tuning lives in feel.ts, and axes.test.ts asserts the behaviour it produces.
  private readonly pitchAxis = new KeyboardAxis(PITCH_AXIS);
  private readonly rollAxis = new KeyboardAxis(ROLL_AXIS);
  private readonly yawAxis = new KeyboardAxis(YAW_AXIS);
  private readonly throttleAxis = new KeyboardAxis(THROTTLE_AXIS);
  private readonly trimAxis = new KeyboardAxis(TRIM_AXIS);
  private readonly rudderTrimAxis = new KeyboardAxis(RUDDER_TRIM_AXIS);
  private readonly aileronTrimAxis = new KeyboardAxis(AILERON_TRIM_AXIS);

  /**
   * The auto-coordination assist. On by default: the aeroplane genuinely needs rudder that
   * varies with power and speed, and a keyboard pilot has neither the axes nor the hands for it.
   * Off is a setting, not a punishment — see `coordination.ts`.
   */
  readonly coordination = new CoordinationAssist();

  private readonly gamepad = new GamepadInput();
  private readonly flapDetentCount: number;
  private readonly mouseYokeSensitivity: number;
  /** The on-screen controls, when there are any. Null on every desktop session. */
  private touch: TouchSource | null = null;

  /** Mouse-yoke deflection, −1…1 in each axis. */
  private mouseYokeX = 0;
  private mouseYokeY = 0;
  private mouseYokeEnabled = false;
  /**
   * Pointer position the yoke is measured against, window-normalised. Captured on the first
   * pointer sample after the yoke is engaged: measuring from the middle of the screen instead
   * meant engaging the yoke with the cursor anywhere near an edge handed the aircraft full
   * deflection in the same instant.
   */
  private yokeOriginX = 0;
  private yokeOriginY = 0;
  private yokeOriginPending = false;

  private flapDetent = 0;
  private parkingBrake = false;

  /**
   * The key in the ignition.
   *
   * Both start true/false in the "aeroplane is running" configuration, because that is how a
   * session spawns — see `sim/propulsion.ts` for why the engine cannot start the milestone dead.
   */
  private ignitionOn = true;
  private starterRequested = false;

  constructor(options: FlightControlsOptions) {
    this.flapDetentCount = Math.max(1, options.flapDetentCount);
    this.mouseYokeSensitivity = options.mouseYokeSensitivity ?? 1;
  }

  /** Is the gear lever down? */
  get gearDown(): boolean {
    return this.commands.gearDown ?? true;
  }

  /**
   * Move the gear lever, and report where it ended up.
   *
   * Deliberately unconditional: this layer does not know which aeroplane is loaded, and it should
   * not — `fdm.ts` gates on `retractableGear !== null`, so a Sparrow or a Tumbler simply ignores
   * the command. Asking the input layer to know about airframes is how the flap and gear paths
   * would start disagreeing about which aircraft can do what.
   *
   * The lever, not the wheels: the FDM animates `gearFraction` over the aircraft's own
   * `travelTimeS`, so this returns the commanded state and not the drawn one.
   */
  toggleGear(): boolean {
    this.commands.gearDown = !this.gearDown;
    return this.commands.gearDown;
  }

  /** Bind the discrete keys. Returns a function that removes every binding. */
  bind(keyboard: Keyboard): () => void {
    const unbinds = [
      keyboard.onPress('KeyF', () => this.cycleFlaps(1)),
      keyboard.onPress('KeyV', () => this.cycleFlaps(-1)),
      keyboard.onPress('KeyY', () => this.toggleMouseYoke()),
      keyboard.onPress('KeyN', () => this.recentreMouseYoke()),
      keyboard.onPress('KeyK', () => {
        this.parkingBrake = !this.parkingBrake;
      }),
      keyboard.onPress('KeyG', () => this.toggleGear()),
    ];
    return () => {
      for (const unbind of unbinds) unbind();
    };
  }

  /** Attach the touch tier, or detach it with `null`. See {@link TouchSource}. */
  setTouchSource(source: TouchSource | null): void {
    this.touch = source;
  }

  /** Is the pilot holding the on-screen fire button? Read by the weapons block in `main.ts`. */
  get touchFire(): boolean {
    return this.touch?.fire ?? false;
  }

  toggleMouseYoke(): void {
    this.setMouseYokeEnabled(!this.mouseYokeEnabled);
  }

  setMouseYokeEnabled(enabled: boolean): void {
    this.mouseYokeEnabled = enabled;
    this.mouseYokeX = 0;
    this.mouseYokeY = 0;
    // Re-centre on wherever the pointer turns out to be, rather than snapping the controls.
    this.yokeOriginPending = enabled;
    this.diagnostics.mouseYokeEnabled = enabled;
  }

  get isMouseYokeEnabled(): boolean {
    return this.mouseYokeEnabled;
  }

  /** Current yoke deflection, −1…1, for a UI indicator. */
  get mouseYokeDeflection(): readonly [number, number] {
    return [this.mouseYokeX, this.mouseYokeY];
  }

  /**
   * Feed the mouse yoke a pointer position normalised to −1…1 across the window.
   * Ignored unless the yoke is enabled.
   *
   * Deflection is measured from the capture point, not from screen centre, and full travel is
   * {@link MOUSE_YOKE.travel} of the normalised window — so the whole usable range is within
   * comfortable reach of wherever the pilot's hand already was.
   */
  setMouseYoke(normalisedX: number, normalisedY: number): void {
    if (!this.mouseYokeEnabled) return;
    if (this.yokeOriginPending) {
      this.yokeOriginX = normalisedX;
      this.yokeOriginY = normalisedY;
      this.yokeOriginPending = false;
    }
    const scale = this.mouseYokeSensitivity / MOUSE_YOKE.travel;
    this.mouseYokeX = clamp((normalisedX - this.yokeOriginX) * scale, -1, 1);
    this.mouseYokeY = clamp((normalisedY - this.yokeOriginY) * scale, -1, 1);
  }

  /** Put the yoke's neutral point back under the pointer, without disengaging it. */
  recentreMouseYoke(): void {
    this.mouseYokeX = 0;
    this.mouseYokeY = 0;
    this.yokeOriginPending = true;
  }

  cycleFlaps(direction: number): void {
    this.flapDetent = clamp(this.flapDetent + Math.sign(direction), 0, this.flapDetentCount - 1);
  }

  get flapDetentIndex(): number {
    return this.flapDetent;
  }

  /** Poll the gamepad. Call once per frame, before {@link updateFixed}. */
  updateFrame(dtSeconds: number): GamepadState {
    const pad = this.gamepad.updateFrame(dtSeconds);
    this.diagnostics.gamepadConnected = pad.connected;
    this.diagnostics.gamepadId = pad.id;

    if (pad.active) {
      if (pad.pressed[GAMEPAD_BUTTON.dpadUp]) this.trimAxis.nudge(0.05);
      if (pad.pressed[GAMEPAD_BUTTON.dpadDown]) this.trimAxis.nudge(-0.05);
      if (pad.pressed[GAMEPAD_BUTTON.x]) this.cycleFlaps(1);
      if (pad.pressed[GAMEPAD_BUTTON.b]) this.cycleFlaps(-1);
    }
    return pad;
  }

  /**
   * Fold every device into the command set for this physics tick.
   *
   * Precedence is "whoever moved last wins, per axis": a gamepad that is being held overrides
   * the keyboard, but an idle gamepad never pins the controls to zero.
   */
  updateFixed(
    keyboard: Keyboard,
    dtSeconds: number,
    feedback?: CoordinationFeedback,
  ): ControlInputs {
    const keyboardPitch = this.pitchAxis.updateFixed(
      keyboard.isDown('ArrowDown') || keyboard.isDown('KeyS'),
      keyboard.isDown('ArrowUp') || keyboard.isDown('KeyW'),
      dtSeconds,
    );
    const keyboardRoll = this.rollAxis.updateFixed(
      keyboard.isDown('ArrowRight') || keyboard.isDown('KeyD'),
      keyboard.isDown('ArrowLeft') || keyboard.isDown('KeyA'),
      dtSeconds,
    );
    const keyboardYaw = this.yawAxis.updateFixed(
      keyboard.isDown('KeyE'),
      keyboard.isDown('KeyQ'),
      dtSeconds,
    );
    const keyboardThrottle = this.throttleAxis.updateFixed(
      keyboard.isDown('Equal') || keyboard.isDown('F2') || keyboard.isDown('ShiftLeft'),
      keyboard.isDown('Minus') || keyboard.isDown('F1') || keyboard.isDown('ControlLeft'),
      dtSeconds,
    );
    const trim = this.trimAxis.updateFixed(
      keyboard.isDown('Period'),
      keyboard.isDown('Comma'),
      dtSeconds,
    );
    const rudderTrim = this.rudderTrimAxis.updateFixed(
      keyboard.isDown('KeyX'),
      keyboard.isDown('KeyZ'),
      dtSeconds,
    );
    // Aileron trim on the bracket-adjacent pair, so the three trims sit under one hand: `,`/`.`
    // pitch, `Z`/`X` rudder, `;`/`'` aileron. Right-wing-down is the positive direction, matching
    // the roll command's sign so "more trim" and "more stick" mean the same way.
    const aileronTrim = this.aileronTrimAxis.updateFixed(
      keyboard.isDown('Quote'),
      keyboard.isDown('Semicolon'),
      dtSeconds,
    );

    const pad = this.gamepad.state;
    const touch = this.touch;
    const commands = this.commands;

    /*
     * Pitch and roll: the on-screen stick first, then the gamepad, then the mouse yoke, then the
     * keyboard.
     *
     * Touch outranks everything because of where it exists: a device showing the stick is a device
     * being flown with a thumb, and if anything else were also moving there it would be an
     * accident — a stray trackpad on a convertible, a gamepad left face-down in a drawer. The
     * `active` gate is what keeps this from being a rule about hardware rather than about who is
     * actually flying.
     */
    if (touch !== null && touch.active) {
      commands.pitch = touch.pitch;
      commands.roll = touch.roll;
      this.diagnostics.activeDevice = 'touch';
    } else if (pad.active && (Math.abs(pad.pitch) > 0.01 || Math.abs(pad.roll) > 0.01)) {
      commands.pitch = pad.pitch;
      commands.roll = pad.roll;
      this.diagnostics.activeDevice = 'gamepad';
    } else if (this.mouseYokeEnabled) {
      // Screen y grows downward, and pulling the mouse back means nose up.
      commands.pitch = shapeAxis(-this.mouseYokeY, { expo: MOUSE_YOKE.expo });
      commands.roll = shapeAxis(this.mouseYokeX, { expo: MOUSE_YOKE.expo });
      this.diagnostics.activeDevice = 'mouse';
    } else {
      commands.pitch = keyboardPitch;
      commands.roll = keyboardRoll;
      this.diagnostics.activeDevice = 'keyboard';
    }

    const touchYaw = touch === null ? 0 : touch.yaw;
    const pilotYaw =
      touchYaw !== 0
        ? touchYaw
        : pad.active && Math.abs(pad.yaw) > 0.01
          ? pad.yaw
          : keyboardYaw;
    // The assist adds to what the pilot asked for and yields to them as they ask for more, so
    // `commands.yaw` stays the single rudder command everything downstream sees. Without
    // feedback there is nothing to coordinate against — a replay or a headless test — and the
    // pilot's input passes through untouched.
    commands.yaw =
      feedback === undefined
        ? pilotYaw
        : this.coordination.updateFixed(pilotYaw, feedback, dtSeconds);
    /*
     * The nosewheel follows the *pilot's* pedal, not the combined command.
     *
     * A real pilot's feet steer and deflect the rudder together, and if the assist's contribution
     * went to both it would be steering the aeroplane — which it is explicitly not for. It is also
     * simply not invertible: the nose tyre saturates within a degree of steer, so the feed-forward
     * cannot compute a pedal position through it. Measured with them coupled, the assist swung the
     * nose right about as hard as the propeller was swinging it left.
     */
    commands.steer = pilotYaw;
    this.diagnostics.coordinationEnabled = this.coordination.enabled;
    this.diagnostics.coordinationOutput = this.coordination.output;

    /*
     * The throttle lever, which is a *position* rather than a deflection.
     *
     * So the rule is different from the stick's: whichever lever the pilot last moved is where the
     * throttle is, and the others are synchronised to it rather than fighting it. A touch throttle
     * reports null while the layer is hidden, which is what keeps a keyboard session on a tablet
     * from having its power pulled to whatever the invisible lever was left at.
     */
    const touchThrottle = touch?.throttle ?? null;
    if (touchThrottle !== null) {
      commands.throttle = touchThrottle;
      this.throttleAxis.set(touchThrottle);
      this.gamepad.setThrottle(touchThrottle);
    } else if (pad.active && pad.throttle !== null) {
      commands.throttle = pad.throttle;
      this.throttleAxis.set(pad.throttle);
    } else {
      commands.throttle = keyboardThrottle;
      this.gamepad.setThrottle(keyboardThrottle);
    }

    commands.pitchTrim = trim;
    commands.yawTrim = rudderTrim;
    /*
     * Aileron trim: the pilot's own, plus whatever the assist has wound in.
     *
     * Added rather than overriding, exactly as the rudder is — so a pilot who wants the wing a
     * shade down can still trim it there and the assist works from that. Clamped because the two
     * together must not exceed the trim's mechanical range.
     */
    commands.rollTrim =
      feedback === undefined
        ? aileronTrim
        : clamp(aileronTrim + this.coordination.updateLateralTrim(commands.roll, feedback, dtSeconds), -1, 1);
    commands.flapDetent = this.flapDetent;

    // Brakes: B for both, comma/period are taken by trim, so differential braking is on the
    // rudder keys while B is held — the way a pedal-less setup has to work.
    const brakesHeld =
      keyboard.isDown('KeyB') ||
      (pad.active && pad.buttons[GAMEPAD_BUTTON.a]) ||
      (touch !== null && touch.brakes);
    const leftBrakeKey = brakesHeld && keyboard.isDown('KeyQ');
    const rightBrakeKey = brakesHeld && keyboard.isDown('KeyE');
    if (leftBrakeKey || rightBrakeKey) {
      commands.brakeLeft = leftBrakeKey ? 1 : 0;
      commands.brakeRight = rightBrakeKey ? 1 : 0;
    } else {
      commands.brakeLeft = brakesHeld ? 1 : 0;
      commands.brakeRight = brakesHeld ? 1 : 0;
    }
    commands.parkingBrake = this.parkingBrake;
    commands.ignition = this.ignitionOn;
    // Cranking with the mags off is a real thing a real starter will happily do and it achieves
    // nothing; gating it here means the sound of a start is never the sound of a start that
    // cannot work.
    commands.starter = this.starterRequested && this.ignitionOn;

    this.diagnostics.mouseYokeEnabled = this.mouseYokeEnabled;
    return commands;
  }

  /**
   * The gamepad's last polled state.
   *
   * Exposed so the menus can navigate with the same pad that flies the aeroplane, without a
   * second `GamepadInput` — two of them would each see the other's button presses as new.
   */
  get gamepadState(): GamepadState {
    return this.gamepad.state;
  }

  /** Current throttle lever position, 0…1 — what the HUD shows. */
  get throttle(): number {
    return this.throttleAxis.raw;
  }

  /** Current rudder trim, −1…1. */
  get rudderTrim(): number {
    return this.rudderTrimAxis.raw;
  }

  /** Current aileron trim, −1…1 — the pilot's own setting, without the assist's contribution. */
  get aileronTrim(): number {
    return this.aileronTrimAxis.raw;
  }

  /** Centre the lateral and directional trims — the "I have made a mess of this" key. */
  centreRudderTrim(): void {
    this.rudderTrimAxis.reset();
    this.aileronTrimAxis.reset();
  }

  /** Turn the auto-coordination assist on or off, and remember the choice. */
  toggleCoordination(storage: Storage | undefined = globalThis.localStorage): boolean {
    const enabled = this.coordination.toggle();
    this.diagnostics.coordinationEnabled = enabled;
    try {
      storage?.setItem(ASSIST_STORAGE_KEY, enabled ? '1' : '0');
    } catch {
      // Private mode or storage disabled: the preference simply is not sticky.
    }
    return enabled;
  }

  /** Restore the remembered assist preference. Defaults to on when nothing was saved. */
  loadPreferences(storage: Storage | undefined = globalThis.localStorage): void {
    try {
      const raw = storage?.getItem(ASSIST_STORAGE_KEY);
      if (raw !== null && raw !== undefined) this.coordination.setEnabled(raw !== '0');
    } catch {
      // Leave the default in place.
    }
    this.diagnostics.coordinationEnabled = this.coordination.enabled;
  }

  /** Reset every axis, e.g. on respawn. */
  /** Mags on or off. Off stops a running engine; on lets a cranking one catch. */
  setIgnition(on: boolean): void {
    this.ignitionOn = on;
    if (!on) this.starterRequested = false;
  }

  get isIgnitionOn(): boolean {
    return this.ignitionOn;
  }

  /** Hold the starter. Driven from the key's *down* state, not from a press, so it is a hold. */
  setStarter(engaged: boolean): void {
    this.starterRequested = engaged;
  }

  reset(): void {
    // A respawn is a fresh aeroplane on the ramp with the engine running, so the key goes back
    // to where it started along with everything else — and so does the gear lever, because a
    // respawn that put you on a runway with the wheels still up would be a very short flight.
    this.ignitionOn = true;
    this.starterRequested = false;
    this.commands.gearDown = true;
    this.pitchAxis.reset();
    this.rollAxis.reset();
    this.yawAxis.reset();
    this.trimAxis.reset();
    this.rudderTrimAxis.reset();
    this.aileronTrimAxis.reset();
    this.coordination.reset();
    this.throttleAxis.set(0);
    this.mouseYokeX = 0;
    this.mouseYokeY = 0;
    // Keep the yoke engaged across a respawn — but recapture its neutral point, so the new
    // aircraft does not inherit whatever deflection the old one was holding.
    this.yokeOriginPending = this.mouseYokeEnabled;
    this.flapDetent = 0;
    this.parkingBrake = false;
  }
}
